import React, { useEffect, useRef, useState } from "react";
import { Mic, Trash2, Send, Lock } from "lucide-react";
import { toast } from "sonner";

const MAX_SECS = 15 * 60;
let sharedStream = null; // se reutiliza el micrófono: iPhone no vuelve a pedir permiso en cada audio

async function getMic() {
  if (sharedStream && sharedStream.getAudioTracks().some((t) => t.readyState === "live")) return sharedStream;
  sharedStream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
  return sharedStream;
}
export function releaseMic() {
  try { sharedStream?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
  sharedStream = null;
}
function pickMime() {
  if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) return "";
  const isApple = /iphone|ipad|ipod|macintosh/i.test(navigator.userAgent) && "ontouchend" in document || /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const list = isApple ? ["audio/mp4", "audio/aac", "audio/webm;codecs=opus", "audio/webm"] : ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
  return list.find((t) => MediaRecorder.isTypeSupported(t)) || "";
}

// Nota de voz estilo WhatsApp: mantén presionado para grabar y suelta para enviar.
// Un toque rápido deja la grabación "fija" (candado) con botones de enviar / borrar. Máximo 15 min.
export default function VoiceRecorder({ onSend, disabled }) {
  const [rec, setRec] = useState(false);
  const [locked, setLocked] = useState(false);
  const [secs, setSecs] = useState(0);
  const mr = useRef(null);
  const chunks = useRef([]);
  const timer = useRef(null);
  const cancelled = useRef(false);
  const pressAt = useRef(0);
  const pressing = useRef(false);

  useEffect(() => () => { clearInterval(timer.current); try { mr.current?.state === "recording" && mr.current.stop(); } catch { /* noop */ } releaseMic(); }, []);

  const finish = (cancel) => {
    cancelled.current = cancel;
    clearInterval(timer.current);
    try { if (mr.current && mr.current.state !== "inactive") mr.current.stop(); } catch { /* noop */ }
    setRec(false); setLocked(false);
  };

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") { toast.error("Este navegador no permite grabar audio"); return false; }
    let stream;
    try { stream = await getMic(); } catch { toast.error("Activa el micrófono en Ajustes del navegador para grabar"); return false; }
    try {
      const type = pickMime();
      const r = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
      chunks.current = []; cancelled.current = false;
      r.ondataavailable = (e) => { if (e.data && e.data.size) chunks.current.push(e.data); };
      r.onstop = () => {
        if (cancelled.current || !chunks.current.length) return;
        const mime = (r.mimeType || type || "audio/mp4").split(";")[0];
        const blob = new Blob(chunks.current, { type: mime });
        if (blob.size < 1200) return; // toque accidental
        onSend(new File([blob], `nota-de-voz.${mime.includes("mp4") || mime.includes("aac") ? "m4a" : "webm"}`, { type: mime }));
      };
      r.start(1000);
      mr.current = r; setRec(true); setSecs(0);
      try { navigator.vibrate?.(30); } catch { /* noop */ }
      timer.current = setInterval(() => setSecs((s) => { if (s + 1 >= MAX_SECS) { finish(false); toast("Se alcanzó el máximo de 15 minutos"); } return s + 1; }), 1000);
      return true;
    } catch { toast.error("No se pudo iniciar la grabación"); return false; }
  };

  const onDown = async (e) => {
    e.preventDefault();
    if (disabled || rec) return;
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* noop */ }
    pressAt.current = Date.now(); pressing.current = true;
    const ok = await start();
    if (ok && !pressing.current) setLocked(true); // se soltó mientras pedía permiso → grabación fija
  };
  const onUp = () => {
    pressing.current = false;
    if (!rec || locked) return;
    if (Date.now() - pressAt.current < 450) setLocked(true); // toque rápido → grabación fija
    else finish(false); // soltar → enviar
  };

  const mm = Math.floor(secs / 60); const ss = String(secs % 60).padStart(2, "0");
  return (
    <>
      <button onPointerDown={onDown} onPointerUp={onUp} onPointerCancel={onUp} onContextMenu={(e) => e.preventDefault()} disabled={disabled}
        data-testid="chat-mic" title="Mantén presionado para grabar"
        className={`w-11 h-11 flex items-center justify-center text-white rounded-full select-none touch-none transition-transform disabled:opacity-40 shadow-lg ${rec ? "bg-red-500 scale-125 shadow-red-500/40" : "bg-gradient-to-br from-navy-light to-navy shadow-navy/30 hover:scale-105"}`}
        style={{ WebkitTouchCallout: "none", WebkitUserSelect: "none" }}>
        <Mic className="w-5 h-5" />
      </button>
      {rec && (
        <div className="absolute inset-0 z-20 flex items-center gap-2 px-3 bg-white/95 backdrop-blur-xl zv-enter" data-testid="chat-recording">
          <button onClick={() => finish(true)} data-testid="chat-rec-cancel" title="Borrar" className="w-10 h-10 rounded-full text-red-500 hover:bg-red-50 flex items-center justify-center"><Trash2 className="w-5 h-5" /></button>
          <span className="zv-rec-dot" />
          <span className="text-sm font-mono text-navy">{mm}:{ss}</span>
          <span className="text-xs text-nardo-400 truncate">{locked ? <><Lock className="inline w-3 h-3 -mt-0.5" /> Grabando · máx. 15 min</> : "Suelta para enviar"}</span>
          {locked && (
            <button onClick={() => finish(false)} data-testid="chat-rec-send" title="Enviar" className="ml-auto w-11 h-11 rounded-full bg-gradient-to-br from-navy-light to-navy text-white flex items-center justify-center hover:scale-105"><Send className="w-4 h-4" /></button>
          )}
        </div>
      )}
    </>
  );
}
